import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { t } from 'ember-intl'
import type WritingLegendsDashboardTableRoute from 'teacher-ui-client/reporting/ui-scope/writing-legends/dashboard-table/route'
import { ReportingControls } from 'teacher-ui-client/components/reporting/controls'
import { ReportingControlsGap } from 'teacher-ui-client/components/reporting/controls-gap'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { ReportingExportSelect, ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import type { ReportData } from 'teacher-ui-client/components/reporting/writing-legends/dashboard/table'
import WritingLegendsDashboardTable from 'teacher-ui-client/components/reporting/writing-legends/dashboard/table'
import type WritingLegendsDashboardTableController from 'teacher-ui-client/reporting/ui-scope/writing-legends/dashboard-table/controller'
import type { ReportingQuerySort, RequestScope } from 'teacher-ui-client/services/reporting'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import { task, timeout } from 'ember-concurrency'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { roundAccurately } from 'teacher-ui-client/utils/round'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type { NormalizedInterval } from 'date-fns'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'

interface Signature {
  Args: {
    model: ModelFor<WritingLegendsDashboardTableRoute>
    controller: WritingLegendsDashboardTableController
  }
}

const hasNumber = (value?: Nullable<number>): value is number => typeof value === 'number'

export class WritingLegendsDashboardTablePage extends Component<Signature> {
  @service intl!: IntlService

  @service reporting!: ReportingService

  @service session!: SessionService

  @service gradeSets!: GradeSetsService

  @service fileDownload!: FileDownload

  @service flashQueue!: FlashQueueService

  get exportOptions() {
    return [{ exportType: ExportType.CSV, exportAction: () => this.exportCsv() }]
  }

  fetchStatsForExport = task(
    { restartable: true },
    async (sort: ReportingQuerySort, period: NormalizedInterval, requestScope: RequestScope) => {
      await timeout(500)

      const url = this.reporting.buildGravityUrl({
        product: Product.WL,
        requestScope,
        reportName: 'dashboard/statistics/students-table',
      })

      const query = this.reporting.buildQuery({ sort, page: 'all', period })

      return this.reporting.fetchReport<ReportData>(url, query)
    },
  )

  async exportCsv() {
    const subscriptionType = SubscriptionType.Writing
    const { uiScope, period } = this.args.model
    const sort = this.args.controller.sort
    const requestScope = toRequestScope(uiScope)
    const isSubco = this.session.currentTeacher.subcoFor(subscriptionType)

    let report: Awaited<ReturnType<typeof this.fetchStatsForExport.perform>>
    try {
      report = await this.fetchStatsForExport.perform(sort, period, requestScope)
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const groupHeader = [
      [this.intl.t('student'), ...Array<string>(isSubco ? 5 : 3).fill('')],
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.excitingWritingExport'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.longWritesExport'),
      [this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.lessons'), '', ''],
      [this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.mapQuizzes'), ''],
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.books'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.totalUsage'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.timeInProgram'),
    ].flat()

    const header = [
      this.intl.t('reporting.firstName'),
      this.intl.t('reporting.lastName'),
      this.intl.t('reporting.dashboardTables.studentExternalId'),
      this.intl.t('reporting.dashboardTables.classes'),
      ...(isSubco ? [this.intl.t('manage.teachers.namePlural'), this.intl.t('grade.label')] : []),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.writingPiecesCompletedExport'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.writingPiecesCompletedExport'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.initialLesson'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.highestLessonCompletedExport'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.totalLessonsCompleted'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.numberOfAttemptsExport'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.avgMapQuizScoreExport'),
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.activitiesCompleted'),
      '',
      this.intl.t('components.reporting.tables.writingLegends.dashboard.headers.totalHours'),
    ]

    const aggregateData = report.aggregates
    const totals = [
      ...[this.intl.t('reporting.dashboardTables.totals'), ...Array<string>(isSubco ? 5 : 3).fill('')],
      aggregateData.writing_pieces_completed,
      aggregateData.long_write_activities_completed,
      '',
      '',
      aggregateData.lessons_mastered,
      aggregateData.map_quiz_attempts_count,
      hasNumber(aggregateData.map_quiz_average_score)
        ? roundAccurately(aggregateData.map_quiz_average_score) + '%'
        : '-',
      aggregateData.book_activities_completed,
      aggregateData.total_usage,
      formatDuration(aggregateData.total_time, '-'),
    ]

    const rows = report.students.map((studentData) => {
      const gradeName = this.gradeSets.findByPosition(studentData.grade_position).name
      return [
        studentData.first_name,
        studentData.last_name,
        studentData.external_id,
        studentData.school_class_names.join(', '),
        ...(isSubco ? [studentData.teacher_names.join(', '), gradeName] : []),
        studentData.writing_pieces_completed ?? '-',
        studentData.long_write_activities_completed ?? '-',
        studentData.min_lesson_mastered ?? '-',
        studentData.max_lesson_mastered ?? '-',
        studentData.lessons_mastered ?? '-',
        studentData.map_quiz_attempts_count ?? '-',
        hasNumber(studentData.map_quiz_average_score) ? roundAccurately(studentData.map_quiz_average_score) + '%' : '-',
        studentData.book_activities_completed ?? '-',
        studentData.total_usage ?? '-',
        formatDuration(studentData.total_time, '-'),
      ]
    })

    const filename = this.intl.t('reporting.writingLegends.dashboard.exportCsvFilename', {
      start: this.intl.formatDate(period.start).replace(/\//g, '-'),
      end: this.intl.formatDate(period.end).replace(/\//g, '-'),
    })

    this.fileDownload.downloadAsCsv([groupHeader, header, totals, ...rows], { filename, header: true })
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.writingLegends"}} />
    <ReportingControls>
      <ReportingUiScopeSelect class="shrink basis-80" />
      <ReportingControlsGap />
      <ReportingPeriodSelect />
      <ReportingControlsGap />
      <ReportingExportSelect @exportOptions={{this.exportOptions}} />
    </ReportingControls>

    <ContentTile>
      <WritingLegendsDashboardTable @period={{@model.period}} @uiScope={{@model.uiScope}} />
    </ContentTile>
  </template>
}

export default RouteTemplate(WritingLegendsDashboardTablePage)
